import './reportUploader.css';
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useApiData } from '../../../../../../contexts/apiService';
import Dropdown, {DropdownOption} from '../../../../../common/dropdown/dropdown';
import { getUniqueDropdownOptions } from "../../../../../../helper_functions";

export interface ReportUploaderHandle {
    openModal: () => void;
}

const ReportUploader = forwardRef<ReportUploaderHandle>((props, ref) => {
    const [isModalOpen, setModalOpen] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [uploadStatus, setUploadStatus] = useState<string>('');
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [carrierOptions, setCarrierOptions] = useState<DropdownOption[]>([]);
    const [selectedCarrier, setSelectedCarrier] = useState<string>('');
    const { apiData, reportUploader } = useApiData()

    useEffect(() => {
        if (apiData?.carrier_definition) {
            const options = getUniqueDropdownOptions({
                tableData: apiData?.carrier_definition,
                valueColumn: 'id',
                labelColumn: 'name'
            });
            setCarrierOptions(options);
        }
    }, [isModalOpen, apiData?.carrier_definition]);

    // Helper to reset uploader state
    const resetUploader = () => {
        setFile(null);
        setUploadProgress(0);
        setUploadStatus('');
    };

    const openModal = () => {
        resetUploader();
        setModalOpen(true);
    };

    useImperativeHandle(ref, () => ({ openModal }));

    const closeModal = () => {
        setModalOpen(false);
        resetUploader();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            setFile(selectedFile);
            setUploadProgress(0);
            setUploadStatus('');
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const droppedFile = e.dataTransfer.files[0];
            setFile(droppedFile);
            setUploadProgress(0);
            setUploadStatus('');
        }
    };

    const handleUpload = async () => {
        if (!file) return;
        
        if (!selectedCarrier) {
            setUploadStatus(`Error: Need to select carrier from dropdown`);
            return;
        }

        try {
            setUploadStatus('Uploading...');
            setUploadProgress(0);
            
            // Call your backend API function
            const result = await reportUploader(file, selectedCarrier);
            console.log(result)
            if (result) {
                if (result?.status === 'error') {
                    setUploadProgress(0);
                    setUploadStatus(`Error: ${result.message}`);
                    return;
                }
                
                setUploadProgress(100);
                setUploadStatus(`Upload complete! ${result.message}`);
            }
            
        } catch (error) {
            console.error(`Error uploading ${file.name}:`, error);
            setUploadStatus('Error during upload');
            setUploadProgress(0);
        }
    };

    const removeFile = () => {
        setFile(null);
        setUploadProgress(0);
        setUploadStatus('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div>
            {isModalOpen && (
                <div className="report-modal-backdrop" onClick={closeModal}>
                    <div className="report-modal-content" onClick={e => e.stopPropagation()}>
                        {/* Fixed Header */}
                        <div className="report-modal-header">
                            <button 
                                className="close-button" 
                                onClick={closeModal}
                                style={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    color: 'red',
                                }}
                            >
                                Close
                            </button>
                            <h2>Upload Report</h2>
                        </div>

                        {/* Scrollable Content */}
                        <div className="report-modal-body">
                            <div 
                                className={`upload-area ${isDragging ? 'drag-active' : ''}`}
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                style={{ cursor: 'pointer' }}
                            >
                                <p>Drag file here or click to select a file</p>
                                <input
                                    type="file"
                                    onChange={handleFileChange}
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    id="file-input"
                                />
                            </div>
                            
                            <Dropdown
                                options={carrierOptions}
                                value={selectedCarrier}
                                onChange={setSelectedCarrier}
                                placeholder="Select Carrier"
                            />

                            {file && (
                                <div className="file-list">
                                    <div className="file-item">
                                        <div className="file-info">
                                            <div className="file-name">{file.name}</div>
                                            <button 
                                                className="remove-file-button"
                                                onClick={removeFile}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: 'red',
                                                    cursor: 'pointer',
                                                    fontSize: '1.2rem'
                                                }}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {uploadProgress > 0 && (
                                        <div className="progress-container">
                                            <div className="progress-bar">
                                                <div 
                                                    className="progress-fill"
                                                    style={{ width: `${uploadProgress}%` }}
                                                />
                                            </div>
                                            <div className="progress-text">
                                                {uploadProgress}%
                                            </div>
                                        </div>
                                    )}
                                    
                                    {uploadStatus && (
                                        <div className={`status ${uploadStatus.includes('Error') ? 'error' : 'success'}`}>
                                            {uploadStatus}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Fixed Footer */}
                        <div className="report-modal-footer">
                            {file && uploadStatus !== 'Upload complete!' && (
                                <button className="upload-button" onClick={handleUpload}>
                                    Upload Report
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});


export default ReportUploader;
